import React, { FormEvent, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import styled from 'styled-components';
import { loadImage } from '../../utils/loadImage';
import { CircularProgress } from '../feedback/CircularProgress';
import { Button } from '../input/Button';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import type { Area } from 'react-easy-crop/types';

type Size = {
    width: number;
    height: number;
};

const FileInput = styled.input.withConfig({ displayName: 'FileInput' }).attrs({
    type: 'file',
    accept: 'image/*',
})`
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
`;

const readImage = (file?: File | null): Promise<string | null> =>
    new Promise((resolve) => {
        if (!file) {
            throw new Error('Изображение не выбрано');
        }

        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = (e) => resolve((e.target?.result as any) || null);
    });

const validateImage = async (src: string, size: Size) => {
    const image = await loadImage(src);

    if (image.width < size.width || image.height < size.height) {
        return `Размеры изображения должны быть не меньше ${size.width}x${size.height}`;
    }

    return undefined;
};

enum FormStep {
    UPLOAD = 'upload',
    CROP = 'crop',
}

const ACCEPT_MAPPINGS = {
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif'],
    'image/jpeg': ['.jpeg', '.jpg'],
};

type FormPayload = {
    step: FormStep;
    error?: string;
    cropArea?: Area;
    imageUrl?: string;
    isPending: boolean;
};

interface BusinessImageFormProps {
    size: Size;
    cropShape?: 'round' | 'rect';
    onSubmit: (imageUrl: string, cropArea: Area) => Promise<void>;
}

export const BusinessImageForm = (props: BusinessImageFormProps) => {
    const { size, cropShape = 'rect' } = props;
    const [payload, setPayload] = useState<FormPayload>({
        step: FormStep.UPLOAD,
        isPending: false,
    });
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const { getRootProps, getInputProps } = useDropzone({
        multiple: false,
        accept: ACCEPT_MAPPINGS,
        onDrop: async ([file]: File[]) => {
            try {
                const imageUrl = await readImage(file);

                if (imageUrl) {
                    const error = await validateImage(imageUrl, size);

                    if (error) {
                        return setPayload({ ...payload, error });
                    }

                    return setPayload({
                        ...payload,
                        imageUrl,
                        step: FormStep.CROP,
                        error: undefined,
                    });
                }
            } catch (_error) {
                /* empty */
            }

            setPayload({ ...payload, error: 'Не удалось обработать изображение' });
        },
    });

    const onCropComplete = useCallback(
        (croppedArea: Area, croppedAreaPixels: Area) => {
            setPayload({ ...payload, cropArea: croppedAreaPixels });
        },
        [payload, setPayload],
    );

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setPayload({ ...payload, isPending: true });
        try {
            await props.onSubmit(payload.imageUrl!, payload.cropArea!);
            setPayload({ ...payload, isPending: false });
        } catch (_error) {
            setPayload({ ...payload, isPending: false, error: 'Не удалось обновить изображение' });
        }
    };

    return (
        <Flex
            as="form"
            width="100%"
            height="100%"
            justifyContent="space-between"
            backgroundColor="var(--background-primary)"
            onSubmit={onSubmit}
        >
            <Flex
                mb={2}
                width="100%"
                height={300}
                borderRadius={{ sm: 1 }}
                overflow="hidden"
                position="relative"
                alignItems="center"
                justifyContent="center"
                backgroundColor="var(--background-secondary)"
                border="2px dashed var(--divider)"
            >
                {payload.step === FormStep.UPLOAD && (
                    <Flex
                        position="absolute"
                        alignItems="center"
                        justifyContent="center"
                        {...getRootProps()}
                    >
                        <Button as="div">Выбрать файл</Button>
                        <FileInput {...getInputProps()} />
                    </Flex>
                )}

                {payload.step === FormStep.CROP && (
                    <Cropper
                        image={payload.imageUrl!}
                        crop={crop}
                        zoom={zoom}
                        cropShape={cropShape}
                        aspect={size.width / size.height}
                        onZoomChange={setZoom}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                    />
                )}
            </Flex>

            {payload.error ? (
                <Typography variant="caption" color="error" mb={2}>
                    {payload.error}
                </Typography>
            ) : null}
            {payload.isPending ? (
                <CircularProgress mx="auto" size={32} />
            ) : (
                <Button
                    width="100%"
                    type="submit"
                    disabled={Boolean(payload.error) || !payload.imageUrl}
                >
                    Сохранить
                </Button>
            )}
        </Flex>
    );
};
